import { getApplicationTenants, selectSite, selectTenant } from './coreApi';
import { decodeTokenClaims } from './authStorage';
import type { AccountType, AuthTokenResponse, CoreTenant } from '../types';
import { sitesForTenant } from '../utils/sites';
import {
  accountTypeFromRoles,
  passportRolesFromClaims,
  rolesFromClaims,
} from '../utils/userDisplay';

function tokenHasRole(accessToken: string, role: AccountType | null): boolean {
  const roles = passportRolesFromClaims(rolesFromClaims(decodeTokenClaims(accessToken)));
  if (!role) return roles.length > 0;
  return roles.includes(role);
}

function tokenHasAnyRole(accessToken: string): boolean {
  return accountTypeFromRoles(rolesFromClaims(decodeTokenClaims(accessToken))) != null;
}

async function bindSite(
  accessToken: string | undefined,
  siteId: string,
): Promise<AuthTokenResponse | null> {
  try {
    return await selectSite(siteId, accessToken);
  } catch {
    return null;
  }
}

/** Asks every site at once and keeps the first token that has the saved role. */
async function bindBestSite(
  tenantId: string,
  tenants: CoreTenant[],
  accessToken: string | undefined,
  preferred: AccountType | null,
): Promise<AuthTokenResponse | null> {
  const sites = await sitesToTry(tenantId, tenants);
  const siteIds = sites
    .map(site => site.tenantSiteId || site.id)
    .filter((siteId): siteId is string => Boolean(siteId));
  if (siteIds.length === 0) return null;

  return new Promise(resolve => {
    let pending = siteIds.length;
    let fallback: AuthTokenResponse | null = null;
    let settled = false;
    const finish = (value: AuthTokenResponse | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    for (const siteId of siteIds) {
      void bindSite(accessToken, siteId).then(bound => {
        if (settled) return;
        if (bound && tokenHasRole(bound.accessToken, preferred)) {
          finish(bound);
          return;
        }
        if (bound && !fallback && tokenHasAnyRole(bound.accessToken)) fallback = bound;
        pending -= 1;
        if (pending === 0) finish(fallback);
      });
    }
  });
}

async function sitesToTry(tenantId: string, tenants: CoreTenant[]): Promise<ReturnType<typeof sitesForTenant>> {
  const known = sitesForTenant(tenants, tenantId);
  if (known.length > 0) return known;
  try {
    return sitesForTenant(await getApplicationTenants(), tenantId);
  } catch {
    return [];
  }
}

/**
 * Binds the organisation. Core's select-tenant only accepts a tenant-wide
 * Passport assignment, so a site-only membership is bound with select-site.
 * Prefers a site that still has the person's saved role.
 */
export async function tokenForTenant(
  tenantId: string,
  tenants: CoreTenant[],
  accessToken: string | undefined,
  preferred: AccountType | null,
): Promise<AuthTokenResponse> {
  const tenantAttempt = selectTenant(tenantId, accessToken).then(
    session => ({ session, error: null as unknown }),
    error => ({ session: null as AuthTokenResponse | null, error }),
  );
  // Site binding starts immediately, so a site-only organisation does not
  // wait out the select-tenant refusal first.
  const siteAttempt = bindBestSite(tenantId, tenants, accessToken, preferred);

  const tenantResult = await tenantAttempt;
  if (tenantResult.session && tokenHasRole(tenantResult.session.accessToken, preferred)) {
    return tenantResult.session;
  }

  const siteSession = await siteAttempt;
  if (siteSession && tokenHasRole(siteSession.accessToken, preferred)) return siteSession;
  if (tenantResult.session && tokenHasAnyRole(tenantResult.session.accessToken)) return tenantResult.session;
  if (siteSession) return siteSession;
  if (tenantResult.session) return tenantResult.session;
  throw tenantResult.error ?? new Error('Could not switch tenant');
}
